import store from '@/store/store';
import { selectKeyframeById } from '@/features/keyframes/keyframesSlice';
import { setSelectedKeyframeId } from '@/features/editorSelection/editorSelectionActions';
import type { Keyframe } from '@/modules/keyframes/types';
import type { RendererBridge } from '@/modules/rendererBridge';
import { isSelectable } from '@/modules/rendererSession/followSelection';

// When the editor last selected a keyframe in the renderer, so a selectionCleared that follows it is ignored
let lastKeyframeSelectionAt = 0;

// Track if we're currently in a drag operation to prevent duplicate selections
const isDragOperation = new Map<string, number>(); // keyframeId -> timestamp

let suppressDeselectUntil = 0;
export const suppressDeselectAll = (ms: number = 1000): void => { suppressDeselectUntil = Date.now() + ms; };
export const isDeselectAllSuppressed = (): boolean => Date.now() < suppressDeselectUntil;

/** Selects a keyframe in the viewport and in Redux; one of a locked or hidden asset is selected in Redux alone, and followSelection clears the viewport. */
export const sendEditorKeyframeSelected = (keyframe: Keyframe, bridge: RendererBridge): void => {
  if (!isSelectable(store.getState(), keyframe.assetInstanceId)) {
    store.dispatch(setSelectedKeyframeId(keyframe.id));
    return;
  }

  // Check if this is a drag operation (recently updated keyframe with same ID)
  const dragTimestamp = isDragOperation.get(keyframe.id);
  if (dragTimestamp && Date.now() - dragTimestamp < 500) {
    // Still update Redux selection state, but don't tell the renderer
    store.dispatch(setSelectedKeyframeId(keyframe.id));
    return;
  }

  // Get previous keyframe from Redux state BEFORE updating (can be from any asset)
  const currentState = store.getState();
  const previousKeyframeId = currentState.editorSelection.selectedKeyframeId;
  const previousKeyframe = previousKeyframeId
    ? selectKeyframeById(currentState, previousKeyframeId) || null
    : null;

  // The same keyframe (a time update included, which keyframeUpdated carries) needs no new selection
  if (previousKeyframe && previousKeyframe.id === keyframe.id) {
    store.dispatch(setSelectedKeyframeId(keyframe.id));
    return;
  }

  lastKeyframeSelectionAt = Date.now();
  bridge.send({ type: 'selectKeyframe', keyframeUniqueId: keyframe.id, assetInstanceUniqueId: keyframe.assetInstanceId });

  // Update Redux with the new selection so subsequent selections have correct previous keyframe
  store.dispatch(setSelectedKeyframeId(keyframe.id));
};

// Whether the editor selected a keyframe in the renderer within the last `withinMs`
export const hasRecentKeyframeSelection = (withinMs: number = 500): boolean =>
  Date.now() - lastKeyframeSelectionAt < withinMs;

// Export function to mark a keyframe as being dragged (to prevent duplicate selections)
export const setDragOperation = (keyframeId: string): void => {
  isDragOperation.set(keyframeId, Date.now());
  // Clean up after 1 second
  setTimeout(() => {
    isDragOperation.delete(keyframeId);
  }, 1000);
};
